/**
 * TRAC-862 (useChatStream slice): the stream-error policy, extracted from the
 * `chunk.type === "error"` branch of `useChatStream`.
 *
 * Why this is its own module: the decision "what does the customer see, and do
 * we try to recover?" was previously spread across a 140-line inline branch with
 * an early `return` in the middle of it. The early return skipped the
 * `scheduleAutoRetry()` call at the bottom, so every non-timeout error was a
 * dead end — no retry, no exhaustion, and therefore no emergency overlay. That
 * was invisible in review because the branch that skipped recovery and the call
 * that performs it were 70 lines apart.
 *
 * Making it a pure function means the policy is stated in one place and is
 * exhaustively testable without rendering a hook.
 *
 * NOT handled here (deliberately): the TRAC-402 `session_auto_cleared` case.
 * That arrives as a *data flag* on the chunk, not as an error string, and it is
 * a deliberate resend rather than a failure — the caller must intercept it
 * before classification.
 */

export interface StreamErrorPolicy {
  /**
   * True when this came from the SSE reader's no-activity watchdog.
   *
   * Load-bearing routing contract (TRAC-81): the watchdog's message must contain
   * "timeout". Don't reword the circuit breaker's error string in
   * `RealChatAgentService` without following this.
   */
  isTimeout: boolean;
  /**
   * Customer-visible copy. Never the raw backend/Bedrock string (TRAC-419) —
   * those leak stack-trace fragments like
   * `'NoneType' object has no attribute 'get'`.
   */
  userMessage: string;
  /** Whether the auto-retry chain (TRAC-550 B8) should be armed for this error. */
  shouldAutoRetry: boolean;
}

/** Copy shown when we can't attribute the failure to a known class. */
export const GENERIC_ERROR_COPY = "Something went wrong. Please try again in a moment.";

export function classifyStreamError(
  rawError: string | undefined | null,
  data?: Record<string, unknown> | null,
): StreamErrorPolicy {
  // TRACI-472: the backend refused this exact message (400/413/422). Retrying the same
  // request cannot succeed, so show its plain copy and do not arm the retry chain.
  if (data?.requestRejected === true && typeof data.userMessage === "string") {
    return { isTimeout: false, userMessage: data.userMessage, shouldAutoRetry: false };
  }
  const raw = rawError || "";
  const lower = raw.toLowerCase();

  const isTimeout = lower.includes("timeout");

  let userMessage: string;
  if (lower.includes("throttl")) {
    userMessage = "Our tire expert is experiencing high demand. Please try again in a moment.";
  } else if (lower.includes("model") && lower.includes("error")) {
    userMessage = "Something went wrong on our end. Please try again in a moment.";
  } else if (lower.includes("service unavailable") || lower.includes("503")) {
    userMessage = "Our tire expert is temporarily unavailable. Please try again shortly.";
  } else {
    userMessage = GENERIC_ERROR_COPY;
  }

  // TRAC-862: recovery is armed for EVERY error, not just timeouts.
  //
  // It is unconditional today, and that is deliberate rather than an oversight:
  // every failure class we can actually observe on this channel is transient
  // (throttle, 503, model error, a crashed agent turn, a dead stream), and we
  // have no signal that distinguishes a permanently-fatal error from a blip.
  // Retrying a truly-dead backend is bounded anyway — MAX_RETRIES caps it and
  // exhaustion hands off to the emergency overlay. Guessing at a "don't bother"
  // class with no evidence is how the old code ended up never retrying at all.
  //
  // Kept as its own field rather than inlined `true` because it answers a
  // genuinely different question from `isTimeout`: `isTimeout` selects the
  // *presentation* (countdown copy vs error bubble), this selects *recovery*.
  // Collapsing them is the exact confusion that produced the original bug.
  const shouldAutoRetry = true;

  return { isTimeout, userMessage, shouldAutoRetry };
}
